import { PureComponent } from 'react';
import { connect } from 'react-redux';
import Raven from 'raven-js';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import VizExLoadingSpinner from 'visitor-ui-component-library/loading/VizExLoadingSpinner';
import { MessageTypes } from 'video-embed/constants';
import { ConversionAssetPositions, PlayerTypes } from 'video-embed/types';
import { debugLog } from 'video-embed/utils/debug';
import { isAuthedAppPreview, isIframed, isJasmine, isSafari, isSupportedPlayerUrl } from 'video-embed/utils/env';
import { reportMessage } from 'video-embed/utils/errors';
import { updateParams } from '../actions/params';
import { confirmVisible, loadVideo } from '../actions/video';
import MessageHandler from '../components/MessageHandler';
import ModalOverlay from '../components/ModalOverlay';
import PlayerError from '../components/PlayerError';
import StaticPosterImage from '../components/StaticPosterImage';
import { PlayerErrorTypes, PlayerStatus } from '../constants';
import { getPlayerType, getVideo, getVideoFetchError, getAreChaptersEnabled } from '../selectors';
import { parseQueryObjectFromUrl } from '../utils/url';
import { Metrics } from '../utils/metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const importEmbedContainer = () => {
  return import( /* webpackChunkName: "EmbedContainer" */'./EmbedContainer').then(module => module.default);
};
class PlayerApp extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      EmbedContainer: null,
      errorLoadingChunk: false,
      hasClickedPlay: false,
      playerReady: false,
      utk: null,
      pageMeta: null,
      currentTime: null,
      appError: undefined
    };
    this.loadingEmbedContainer = false;
    this.trackerQueue = [];
    this.loadEmbedContainer = (hasClickedPlay = false) => {
      this.setState({
        hasClickedPlay
      });
      if (this.state.EmbedContainer || this.loadingEmbedContainer) {
        return;
      }
      this.loadingEmbedContainer = true;
      importEmbedContainer().then(EmbedContainer => {
        Raven.captureBreadcrumb({
          level: 'info',
          message: 'Loaded EmbedContainer chunk'
        });
        this.setState({
          EmbedContainer
        });
        if (hasClickedPlay && !isJasmine()) {
          setTimeout(this.ensurePlaying, 1000);
        }
      }).catch(err => {
        console.error('Failed to load EmbedContainer chunk', err);
        Raven.captureBreadcrumb({
          level: 'error',
          message: 'Failed to load EmbedContainer chunk'
        });
        Raven.captureException(err, {
          fingerprint: ['EmbedContainer-chunk-load-failed']
        });
        this.setState({
          errorLoadingChunk: true
        });
      });
    };
    this.ensurePlaying = async () => {
      this.removeLocalStorageKeys();
      if (this.state.hasClickedPlay && !this.state.playerReady) {
        if (this.videoJsPlayer) {
          debugLog('Playback has not started after loading chunk, calling play()');
          await this.videoJsPlayer.play();
        } else {
          reportMessage('VideoJS player not found in ensurePlaying');
        }
      }
    };
    this.postMessageToParent = (messageType, payload) => {
      if (this.handlerRef && this.handlerRef.postMessageToParent) {
        this.handlerRef.postMessageToParent(messageType, payload);
      }
    };
    this.onVideoLoaded = () => {
      const {
        video
      } = this.props;
      const videoTitle = video.title;
      const titleKey = videoTitle ? 'videoPlayer.htmlTitleWithVideo' : 'videoPlayer.htmlTitle';
      setDocumentTitle(titleKey, {
        videoTitle
      });
      this.postMessageToParent(MessageTypes.VIDEO_LOADED, video);
      this.props.tracker.setProperties({
        playerType: this.getPlayerType(),
        videoId: video.videoId,
        videoTitle: video.title,
        isEmbeddable: Boolean(video.isEmbeddable),
        hasPrimaryPlaybackId: Boolean(video.primaryPlaybackId)
      });
    };
    this.onCreatePlayer = (playerContainer, videoJsPlayer) => {
      this.playerContainer = playerContainer;
      this.videoJsPlayer = videoJsPlayer;
      // @ts-expect-error videoJsPlayer is not defined in the window object
      window.videoJsPlayer = videoJsPlayer; // just to support console debugging

      if (isSafari() && !this.props.params.muted) {
        videoJsPlayer.muted(false);
      }
      videoJsPlayer.on('playing', () => {
        document.body.classList.add('player-played');
        if (!this.state.playerReady) {
          debugLog('Code-split loaded and play event fired');
          this.setState({
            playerReady: true
          });
        }
      });
    };
    this.canTrack = () => {
      if (isAuthedAppPreview()) {
        return true;
      }
      return Boolean(this.state.utk);
    };
    this.trackUsage = (eventKey, eventProperties) => {
      Raven.captureBreadcrumb({
        category: 'usage',
        message: `${eventKey} - ${JSON.stringify(eventProperties)}`
      });
      if (this.canTrack()) {
        this.props.tracker.track(eventKey, eventProperties);
      } else {
        debugLog('Queuing usage event', eventKey, eventProperties);
        this.trackerQueue.push([eventKey, eventProperties]);
      }
    };
    this.handleSetPageMeta = pageMeta => {
      if (pageMeta && pageMeta.pageUrl) {
        try {
          const queryParams = parseQueryObjectFromUrl(pageMeta.pageUrl);
          if (queryParams && queryParams._hsenc) {
            pageMeta._hsenc = queryParams._hsenc;
          }
        } catch (e) {
          console.error('Error thrown parsing pageUrl query params', e);
          Metrics.counter('error-parsing-page-meta-url').increment();
        }
        Raven.setExtraContext(pageMeta);
      }
      this.setState({
        pageMeta
      });
    };
    this.handleSetCurrentTime = currentTime => {
      this.loadEmbedContainer();
      this.setState({
        currentTime
      });
    };
    this.handleSetPlaybackRate = playbackRate => {
      if (this.videoJsPlayer) {
        this.videoJsPlayer.playbackRate(playbackRate);
      }
    };
    this.handleTriggerAutoPlay = () => {
      this.loadEmbedContainer(true);
    };
    this.handleUpdateDisplayOptions = (options = {}) => {
      const {
        params
      } = this.props;
      if (!this.videoJsPlayer) {
        return;
      }
      if (typeof options.hiddenControls === 'boolean' && options.hiddenControls !== params.hiddenControls) {
        this.videoJsPlayer.controls(!options.hiddenControls);
      }
      if (options.conversionAsset) {
        this.props.updateParams({
          conversionAsset: options.conversionAsset
        });
        if (options.conversionAsset.position === ConversionAssetPositions.PRE) {
          this.handleSetCurrentTime(0);
        } else if (options.conversionAsset.position === ConversionAssetPositions.POST) {
          this.handleSetCurrentTime(this.videoJsPlayer.duration());
        }
      }
    };
    this.handleUpdatePlayerStatus = async status => {
      if (!this.videoJsPlayer) {
        return;
      }
      if (status === PlayerStatus.PAUSED && !this.videoJsPlayer.paused()) {
        this.videoJsPlayer.pause();
        this.trackUsage('playerInteraction', {
          action: 'paused-for-other-player'
        });
      } else if (status === PlayerStatus.PLAYING && this.videoJsPlayer.paused()) {
        await this.videoJsPlayer.play();
        this.trackUsage('playerInteraction', {
          action: 'play-triggered'
        });
      }
    };
    this.handleConfirmVisible = () => {
      this.props.confirmVisible();
      this.trackUsage('playerInteraction', {
        action: 'confirmed-visible'
      });
    };
    this.setUtk = utk => {
      const {
        tracker
      } = this.props;
      if (utk) {
        tracker.setProperties({
          hasUtk: true
        });
        this.trackQueuedEvents();
        document.body.classList.add('player-with-utk');
      } else {
        // utk is being unset by cookie consent revocation
        if (this.reporter) {
          this.reporter.utk = null;
        }
        tracker.setProperties({
          hasUtk: false
        });
        this.removeLocalStorageKeys();
        document.body.classList.remove('player-with-utk');
      }
      this.setState({
        utk
      });
    };
    this.renderError = () => {
      const {
        videoFetchError
      } = this.props;
      const {
        appError
      } = this.state;
      return /*#__PURE__*/_jsx(PlayerError, {
        appError: appError,
        videoFetchError: videoFetchError,
        hasPlaybackStarted: false
      });
    };
  }
  componentDidMount() {
    const {
      params,
      areChaptersEnabled
    } = this.props;
    this.props.loadVideo(params.id, params);
    this.removeLocalStorageKeys();

    // we disabled code split for safari in 2023 due to concern we couldn't reliably start the video after loading without another split
    // now using the chapters gate in order to test backing out of this, for a potentially major latency boost https://git.hubteam.com/HubSpot/hubspot-video-ui/pull/1342
    if (params.autoplay || isSafari() && !areChaptersEnabled) {
      debugLog(`Loading EmbedContainer code split immediately due to ${params.autoplay ? 'autoplay' : 'safari'}`);
      this.loadEmbedContainer(params.autoplay);
    }
    if (!isSupportedPlayerUrl()) {
      this.handleError(`Unrecognized player domain: ${window.location.hostname}`, PlayerErrorTypes.INVALID_URL);
      return;
    }
    if (!params.id || !params.portalId) {
      this.handleError('Player not passed id or portalId', PlayerErrorTypes.INVALID_URL);
      return;
    }
    if (isIframed() && !isAuthedAppPreview() && params.playerType !== PlayerTypes.ACADEMY_IN_APP && params.playerType !== PlayerTypes.ACADEMY_EMBEDDED) {
      setTimeout(() => {
        if (!this.state.pageMeta) {
          this.handleError('Timed out waiting for SET_PAGE_META message', PlayerErrorTypes.UNSUPPORTED_EMBED);
          this.postMessageToParent(MessageTypes.PLAYER_ORPHANED);
        }
      }, 5000);
    } else if (!this.isValidEmbed()) {
      this.handleError('Public player visited directly', PlayerErrorTypes.UNSUPPORTED_EMBED);
    }
  }
  componentDidUpdate(prevProps) {
    const {
      video,
      videoFetchError
    } = this.props;
    if (video && !prevProps.video) {
      this.onVideoLoaded();
    }
    if (videoFetchError && !prevProps.videoFetchError) {
      if (isAuthedAppPreview() || this.props.params.playerType === PlayerTypes.ACADEMY_IN_APP || this.props.params.playerType === PlayerTypes.ACADEMY_EMBEDDED) {
        this.postMessageToParent(MessageTypes.PLAYER_ERROR, {
          errorType: PlayerErrorTypes.VIDEO_NOT_FOUND,
          payload: 'Video not found'
        });
      }
    }
  }
  isValidEmbed() {
    const {
      params,
      debugFlagEnabled
    } = this.props;
    const {
      pageMeta
    } = this.state;
    return isAuthedAppPreview() || params.playerType === PlayerTypes.RICH_TEXT_PREVIEW || params.playerType === PlayerTypes.EDITOR_PREVIEW || params.playerType === PlayerTypes.ACADEMY_IN_APP || params.playerType === PlayerTypes.ACADEMY_EMBEDDED || Boolean(pageMeta && pageMeta.pageUrl) || debugFlagEnabled;
  }
  getPlayerType() {
    const {
      playerType,
      video
    } = this.props;
    if (playerType) {
      return playerType;
    }
    if (video && (!video.isEmbeddable || !video.primaryPlaybackId)) {
      return PlayerTypes.VIDEOJS_RAW_URL;
    }
    return PlayerTypes.DEFAULT;
  }
  handleError(message, errorType) {
    debugLog(message, errorType);
    Metrics.counter('videojs-error', {
      errorType
    }).increment();
    if (errorType === PlayerErrorTypes.UNSUPPORTED_EMBED && isIframed()) {
      // not locking this down quite yet while we look over sentries from complex sites
      return;
    }
    this.setState({
      appError: errorType
    });
    if (isAuthedAppPreview() || this.props.params.playerType === PlayerTypes.ACADEMY_IN_APP || this.props.params.playerType === PlayerTypes.ACADEMY_EMBEDDED) {
      this.postMessageToParent(MessageTypes.PLAYER_ERROR, {
        errorType,
        message
      });
    }
  }
  trackQueuedEvents() {
    const {
      tracker
    } = this.props;
    if (this.trackerQueue.length) {
      debugLog('Sending usage trackerQueue', this.trackerQueue);
      this.trackerQueue.forEach(([eventKey, eventProperties]) => tracker.track(eventKey, eventProperties));
      this.trackerQueue = [];
    }
  }
  removeLocalStorageKeys() {
    // ensure usage-tracker-js / VideoJS do not persist any localStorage keys in the event we cannot confirm cookie consent
    // see https://issues.hubspotcentral.com/browse/VEP-2261 & https://issues.hubspotcentral.com/browse/VEP-6442
    try {
      // two keys below should stop being set with https://git.hubteam.com/HubSpot/hubspot-video-ui/pull/1562, and OK to stop removing down the road
      localStorage.removeItem('__hmpl');
      localStorage.removeItem('HUBLYTICS_EVENTS_53');
      // set by videojs, not yet implicated in a privacy JIRA but removing to keep localStorage clear just in case
      localStorage.removeItem('loglevel');
    } catch (e) {
      debugLog('Failed to remove localStorage keys', e);
    }
  }
  renderMessageHandler() {
    const {
      embedId,
      params,
      video
    } = this.props;
    if (!isIframed() || !params.parentOrigin) {
      return null;
    }
    return /*#__PURE__*/_jsx(MessageHandler, {
      setRef: handlerRef => {
        this.handlerRef = handlerRef;
      },
      embedId: embedId,
      video: video,
      parentOrigin: params.parentOrigin,
      onSetPageMeta: this.handleSetPageMeta,
      onSetUtk: this.setUtk,
      onSetCurrentTime: this.handleSetCurrentTime,
      onSetPlaybackRate: this.handleSetPlaybackRate,
      onTriggerAutoPlay: this.handleTriggerAutoPlay,
      onUpdateDisplayOptions: this.handleUpdateDisplayOptions,
      onUpdatePlayerStatus: this.handleUpdatePlayerStatus,
      onSubmitOverlay: overlayInfo => this.playerContainer.handlePostConversion(overlayInfo),
      onSkipOverlay: overlayInfo => this.playerContainer.handleSkipOverlay(overlayInfo),
      onConfirmVisible: this.handleConfirmVisible
    });
  }
  renderLoadingOverlay() {
    if (this.state.hasClickedPlay) {
      if (this.state.playerReady || this.state.errorLoadingChunk) {
        return null;
      }
    } else if (this.props.video) {
      return null;
    }
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ModalOverlay, {
        allowClose: false,
        className: "loading-overlay",
        use: "lightbox",
        children: /*#__PURE__*/_jsx(VizExLoadingSpinner, {
          grow: true,
          size: "md",
          "data-test-id": "player-loading"
        })
      })
    );
  }
  renderPoster() {
    const {
      params,
      video
    } = this.props;
    if (this.state.errorLoadingChunk) {
      return this.renderError();
    }
    if (!video) {
      return null;
    }
    return /*#__PURE__*/_jsx(StaticPosterImage, {
      options: params,
      playerType: PlayerTypes.EDITOR_PREVIEW,
      video: video,
      onClickPoster: () => {
        debugLog('Poster image play button clicked');
        if (this.getPlayerType() !== PlayerTypes.RICH_TEXT_PREVIEW) {
          this.loadEmbedContainer(true);
        }
      },
      currentTime: this.state.currentTime || 0
    });
  }
  render() {
    const {
      params,
      videoFetchError
    } = this.props;
    const {
      EmbedContainer,
      appError
    } = this.state;
    if (videoFetchError || appError) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [this.renderError(), this.renderMessageHandler()]
      });
    }
    if (!EmbedContainer || !this.props.video) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [this.renderPoster(), this.renderLoadingOverlay(), this.renderMessageHandler()]
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(EmbedContainer, Object.assign({}, this.props, {
        params: Object.assign({}, params, {
          autoplay: this.state.hasClickedPlay || params.autoplay
        }),
        utk: this.state.utk,
        pageMeta: this.state.pageMeta,
        playerType: this.getPlayerType(),
        currentTime: this.state.currentTime,
        postMessageToParent: this.postMessageToParent,
        onCreatePlayer: this.onCreatePlayer,
        trackUsage: this.trackUsage
      })), this.renderLoadingOverlay(), this.renderMessageHandler()]
    });
  }
}
const mapStateToProps = state => ({
  playerType: getPlayerType(state),
  video: getVideo(state),
  videoFetchError: getVideoFetchError(state),
  areChaptersEnabled: getAreChaptersEnabled(state)
});
const mapDispatchToProps = {
  loadVideo,
  confirmVisible,
  updateParams
};
export default connect(mapStateToProps, mapDispatchToProps)(PlayerApp);